import { useCallback } from 'react';

import type { TabBarItemProps } from './BottomTabBar.types';

import { NotificationDot } from '@/components/ui/NotificationDot';

/**
 * Individual tab item for the BottomTabBar.
 * Displays an icon (24px) and label (12px) with active/inactive states.
 */
export function TabBarItem({ item, active = false, onClick }: TabBarItemProps) {
  const handleClick = useCallback(() => {
    onClick?.(item);
  }, [item, onClick]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick?.(item);
      }
    },
    [item, onClick]
  );

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={item.label}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`
        flex flex-1 flex-col items-center justify-center gap-0.5
        py-2 px-1
        transition-colors duration-fast
        focus:outline-none focus-visible:ring-state-focus-inset
        ${active ? 'text-primary-300' : 'text-neutral-500'}
      `}
    >
      {/* Icon container - 24px */}
      <span
        className={`
          relative flex h-6 w-6 items-center justify-center
          [&>svg]:h-6 [&>svg]:w-6
        `}
      >
        {item.icon}
        {/* Badge indicator */}
        {item.badge !== undefined && (
          <NotificationDot
            count={item.badge}
            tone={active ? 'primary' : 'error'}
            className="absolute -right-1 -top-1"
          />
        )}
      </span>

      {/* Label - 10px */}
      <span
        className={`
          text-label-xs font-medium
          transition-colors duration-fast
          ${active ? 'text-primary-300' : 'text-neutral-500'}
        `}
      >
        {item.label}
      </span>

      {/* Active indicator dot */}
      {active && (
        <span
          className="absolute bottom-1 h-1 w-1 rounded-full bg-primary-300"
          aria-hidden="true"
        />
      )}
    </button>
  );
}
